<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue';
import { buildPreflightReport, type PreflightIssue } from './engine/preflight';
import {
  preflightExpanded,
  mobilePreflightExpanded,
  preflightOverlayVisible,
  togglePreflight,
  closePreflight,
  toggleMobilePreflight,
  togglePreflightOverlay,
} from './uiState';
import { useConfigStore } from '@designer/stores/configStore';
import { useDesignStore } from '@designer/stores/designStore';
import { useProductStore } from '@designer/stores/productStore';
import { useToolsStore } from '@designer/stores/toolsStore';
import { useUiStore } from '@designer/stores/uiStore';
import { rehydrateSvgItem } from '@designer/composables/useSvgRecolor';
import type { DesignItem } from '@core/types';
import { resolveViewAreaSizes, resolveViewPhysicalSizesCm } from '@designer/utils/printArea';
import { resolvePrintingMethodProviderId } from '@core/extensionRegistry';
import { t } from './i18n';

const props = withDefaults(defineProps<{ mobile?: boolean }>(), { mobile: false });

// This addon bundles its own copy of pinia (only `vue` is externalized), so
// useXStore() with no argument can't find the host's active instance via
// inject() — the host pinia must be passed explicitly.
const pinia = window.Veyop!.designer.pinia!;
const config = useConfigStore(pinia);
const design = useDesignStore(pinia);
const product = useProductStore(pinia);
const tools = useToolsStore(pinia);
const ui = useUiStore(pinia);

const preflightDefaults = computed(
  () => (config.data.items as { preflightDefaults?: { showStatusPill?: boolean } }).preflightDefaults,
);
const enabled = computed(() => preflightDefaults.value?.showStatusPill !== false);

// Text is measured with the fonts the page has; web fonts landing later change the result.
const fontsTick = ref(0);
const bumpFonts = () => { fontsTick.value++; };
document.fonts?.addEventListener?.('loadingdone', bumpFonts);
onBeforeUnmount(() => document.fonts?.removeEventListener?.('loadingdone', bumpFonts));

const report = computed(() => {
  void fontsTick.value;
  const stageWidth = Number(config.data.items.boxWidth) || 500;
  const stageHeight = Number(config.data.items.boxHeight) || 500;
  return buildPreflightReport(design.items, {
    stageWidth,
    stageHeight,
    areaSizes: resolveViewAreaSizes(product.product?.areas, stageWidth, stageHeight),
    physicalSizesCm: resolveViewPhysicalSizesCm(product.product?.params),
    profile: config.data.items.preflightProfile,
    activePrintCode: product.selectedPrintingCode,
    activePrintMethodPack: resolvePrintingMethodProviderId(product.selectedPrinting),
    embroideryProfile: config.data.items.embroideryProfile,
    garmentColors: product.colorHexList,
    garmentHasTexture: Boolean(product.activeColor?.textureUrl),
    measureContent: (item) => window.Veyop?.itemBounds?.measureContent?.(item) ?? null,
  });
});

const errorCount = computed(() => report.value.issues.filter((issue) => issue.severity === 'error').length);
const warningCount = computed(() => report.value.issues.filter((issue) => issue.severity === 'warning').length);
const label = computed(() => {
  if (report.value.status === 'idle') return t('Artwork check');
  if (report.value.status === 'pass') return t('Ready to print');
  if (report.value.status === 'error') {
    const text = report.value.enforcement === 'block' ? t('Fix before checkout') : t('Fix artwork');
    return `${text} (${errorCount.value})`;
  }
  return `${t('Needs attention')} (${warningCount.value})`;
});
const groups = computed(() => {
  const out = new Map<string, PreflightIssue[]>();
  for (const issue of report.value.issues) {
    const list = out.get(issue.view) ?? [];
    list.push(issue);
    out.set(issue.view, list);
  }
  return [...out.entries()].map(([view, issues]) => [view, [...issues].sort((a, b) => (a.severity === b.severity ? 0 : a.severity === 'error' ? -1 : 1))] as const);
});

function replace(template: string, values: Record<string, string | number>): string {
  return Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`{${key}}`, String(value)), template);
}

/** Plain-language headline + one-line reason, written for shoppers rather than print operators. */
function issueCopy(issue: PreflightIssue): { title: string; why: string } {
  switch (issue.code) {
    case 'empty_design': return { title: t('Nothing to check yet'), why: t('Add text, a photo or a graphic and we will check it for print.') };
    case 'low_dpi': return {
      title: t('This photo may print blurry'),
      why: replace(t('At this size it is {actual} DPI; {target} DPI prints sharp. Make it smaller or use a larger photo.'), { actual: issue.actualDpi ?? '?', target: report.value.outputDpi }),
    };
    case 'outside_print_bounds': return { title: t('Part of your design is outside the print area'), why: t('Anything outside the dashed box will not be printed.') };
    case 'outside_safe_area': return { title: t('Too close to the edge'), why: replace(t('Keep artwork {margin} mm away from the edge so it is not trimmed off.'), { margin: report.value.safetyMm }) };
    case 'insufficient_bleed': return { title: t('Background does not reach the edges'), why: replace(t('Stretch it {bleed} mm past the edge so no white border shows after trimming.'), { bleed: report.value.bleedMm }) };
    case 'exclusion_zone_collision': return { title: t('Covers an area that cannot be printed'), why: t('Move it away from seams, buttons or other marked no-print zones.') };
    case 'min_text_size': return { title: t('Text is very small'), why: t('It may be hard to read once printed.') };
    case 'low_contrast': return { title: t('Hard to see on this product color'), why: t('The color is almost the same as the product, so it may disappear.') };
    case 'semi_transparent': return { title: t('See-through layer'), why: t('Transparency can print as visible dots. Solid colors print cleanest.') };
    case 'text_overflow': return { title: t('Text does not fit its box'), why: t('Part of the text is cut off. Resize the box or shorten the text.') };
    default: return { title: t(issue.message.replace(/^[^:]+:\s*/, '')), why: '' };
  }
}

const FIX_LABELS: Record<string, () => string> = {
  fit_print_area: () => t('Fit inside print area'),
  fit_safe_area: () => t('Move away from the edge'),
  shrink_sharp: () => t('Shrink to print sharp'),
  bigger_text: () => t('Make text bigger'),
  contrast_color: () => t('Use a contrasting color'),
  opaque: () => t('Make fully opaque'),
  fit_text_box: () => t('Fit box to text'),
};

function fixLabel(issue: PreflightIssue): string {
  const label = issue.fix?.label ?? '';
  return FIX_LABELS[label]?.() ?? label;
}

function findItem(issue: PreflightIssue): DesignItem | undefined {
  return (design.items[issue.view] ?? []).find((item) => item.id === issue.itemId);
}

function layerName(issue: PreflightIssue): string {
  const item = findItem(issue);
  if (!item) return '';
  if (item.type === 'text') {
    const text = String(item.text ?? '').trim();
    return text ? `“${text.length > 24 ? `${text.slice(0, 24)}…` : text}”` : t('Text');
  }
  if (item.type === 'image') return t('Photo');
  return t('Graphic');
}

/**
 * One fix per layer: each patch is computed from the layer's current geometry, so stacking two
 * (e.g. "shrink for DPI" + "fit inside") would let the second undo the first. Errors go first;
 * anything left re-appears in the refreshed report.
 */
const fixable = computed(() => {
  const seen = new Set<string>();
  return [...report.value.issues]
    .filter((issue) => issue.fix && issue.itemId)
    .sort((a, b) => (a.severity === b.severity ? 0 : a.severity === 'error' ? -1 : 1))
    .filter((issue) => {
      const key = `${issue.view}:${issue.itemId}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
});

function showMe(issue: PreflightIssue) {
  design.switchView(issue.view);
  if (issue.itemId) {
    design.selectItem(issue.itemId);
    ui.flashItem(issue.itemId);
  }
  if (props.mobile) mobilePreflightExpanded.value = false;
  else closePreflight();
}

async function applyFix(issue: PreflightIssue) {
  if (!issue.fix || !issue.itemId) return;
  design.updateItem(issue.itemId, issue.fix.patch as Partial<DesignItem>, issue.view);
  if (issue.fix.rehydrateSvg) {
    const item = findItem(issue);
    if (item) await rehydrateSvgItem(item).catch(() => undefined);
  }
}

/** One history entry per click, so a single Undo reverts the repair. */
async function fix(issues: PreflightIssue[]) {
  tools.beginHistoryBatch();
  try {
    // Issues are recomputed after each patch; apply the snapshot taken at click time, one per item+code.
    for (const issue of issues) await applyFix(issue);
  } finally {
    tools.endHistoryBatch('preflight-fix');
  }
  const last = issues[issues.length - 1];
  if (last?.itemId) {
    design.switchView(last.view);
    design.selectItem(last.itemId);
    ui.flashItem(last.itemId);
  }
}
</script>

<template>
  <div v-if="enabled" class="vptw:relative vptw:shrink-0">
    <button
      type="button"
      class="vptw:min-h-7 vptw:rounded-full! vptw:border-0! vptw:px-2.5 vptw:py-1 vptw:text-[11px] vptw:font-semibold vptw:focus-visible:ring-2! vptw:focus-visible:ring-(--vs-accent)! vptw:cursor-pointer"
      :class="report.status === 'error' ? 'vptw:bg-red-100! vptw:text-red-700!' : report.status === 'warning' ? 'vptw:bg-amber-100! vptw:text-amber-800!' : report.status === 'pass' ? 'vptw:bg-green-100! vptw:text-green-700!' : 'vptw:bg-gray-100! vptw:text-(--vs-ink-2)!'"
      :aria-expanded="mobile ? mobilePreflightExpanded : preflightExpanded"
      @click="mobile ? toggleMobilePreflight() : togglePreflight()"
    >
      {{ label }}
    </button>

    <div
      v-if="mobile ? mobilePreflightExpanded : preflightExpanded"
      class="vptw:z-[90] vptw:w-[min(22rem,calc(100vw-1rem))] vptw:rounded-xl vptw:border vptw:border-(--vs-line) vptw:bg-white vptw:p-3 vptw:text-xs vptw:text-(--vs-ink-2) vptw:shadow-xl"
      :class="mobile ? 'vptw:fixed vptw:bottom-16 vptw:left-2' : 'vptw:absolute vptw:bottom-9 vptw:left-0'"
    >
      <div class="vptw:flex vptw:items-start vptw:justify-between vptw:gap-2">
        <div>
          <p class="vptw:m-0! vptw:font-semibold vptw:text-(--vs-ink)">{{ t('Print readiness') }}</p>
          <p class="vptw:mt-0.5! vptw:mb-0! vptw:text-[11px] vptw:text-(--vs-ink-2)">{{ report.outputDpi }} {{ t('DPI target') }} · {{ errorCount }} {{ t('errors') }} · {{ warningCount }} {{ t('warnings') }}</p>
        </div>
        <button type="button" class="vptw:border-0! vptw:bg-transparent! vptw:p-0! vptw:text-(--vs-ink-3) vptw:cursor-pointer" :aria-label="t('Close artwork check')" @click="mobile ? toggleMobilePreflight() : closePreflight()">✕</button>
      </div>
      <button type="button" class="vptw:mt-2 vptw:w-full vptw:rounded-lg vptw:border vptw:border-(--vs-line) vptw:bg-gray-50 vptw:px-3 vptw:py-2 vptw:text-left vptw:text-xs vptw:font-medium vptw:text-(--vs-ink-2) vptw:cursor-pointer" @click="togglePreflightOverlay()">
        {{ preflightOverlayVisible ? t('Hide production overlay') : t('Show production overlay') }}
      </button>
      <p v-if="report.status === 'pass'" class="vptw:mt-2 vptw:mb-0! vptw:rounded-lg vptw:bg-green-500/10 vptw:px-2.5 vptw:py-2 vptw:text-green-600">✓ {{ t('Looks good — your design is ready to print.') }}</p>
      <p v-else-if="report.status === 'idle'" class="vptw:mt-2 vptw:mb-0! vptw:text-(--vs-ink-2)">{{ t('Add artwork to run checks.') }}</p>
      <template v-else>
        <button
          v-if="fixable.length > 1"
          type="button"
          class="vptw:mt-2 vptw:w-full vptw:rounded-lg! vptw:border-0! vptw:bg-(--vs-accent)! vptw:px-3 vptw:py-2 vptw:text-xs vptw:font-semibold vptw:text-white! vptw:cursor-pointer"
          @click="fix(fixable)"
        >
          {{ t('Fix all') }} ({{ fixable.length }})
        </button>
        <div class="vptw:mt-1 vptw:max-h-[min(60vh,26rem)] vptw:overflow-y-auto">
          <div v-for="[view, issues] in groups" :key="view" class="vptw:mt-2">
            <p v-if="groups.length > 1" class="vptw:m-0! vptw:text-[10px] vptw:font-semibold vptw:uppercase vptw:tracking-wide vptw:text-(--vs-ink-3)">{{ view }}</p>
            <div
              v-for="issue in issues"
              :key="`${issue.code}-${issue.itemId}-${issue.zoneId || ''}`"
              class="vptw:mt-1.5 vptw:rounded-lg vptw:border vptw:px-2.5 vptw:py-2"
              :class="issue.severity === 'error' ? 'vptw:border-red-500/30 vptw:bg-red-500/10' : 'vptw:border-amber-500/30 vptw:bg-amber-500/10'"
            >
              <div class="vptw:flex vptw:items-start vptw:gap-2">
                <span class="vptw:mt-1 vptw:size-2 vptw:shrink-0 vptw:rounded-full" :class="issue.severity === 'error' ? 'vptw:bg-red-500' : 'vptw:bg-amber-500'" :aria-label="issue.severity === 'error' ? t('Must fix') : t('Worth checking')" />
                <div class="vptw:min-w-0 vptw:flex-1">
                  <p class="vptw:m-0! vptw:text-xs vptw:font-semibold vptw:leading-4 vptw:text-(--vs-ink)">{{ issueCopy(issue).title }}</p>
                  <p v-if="issueCopy(issue).why" class="vptw:mt-0.5! vptw:mb-0! vptw:text-[11px] vptw:leading-4 vptw:text-(--vs-ink-2)">{{ issueCopy(issue).why }}</p>
                  <p v-if="layerName(issue)" class="vptw:mt-1! vptw:mb-0! vptw:text-[10px] vptw:text-(--vs-ink-3)">{{ t('Layer') }}: {{ layerName(issue) }}</p>
                </div>
              </div>
              <div v-if="issue.itemId" class="vptw:mt-2 vptw:flex vptw:flex-wrap vptw:gap-1.5 vptw:pl-4">
                <button type="button" class="vptw:rounded-md! vptw:border! vptw:border-(--vs-line)! vptw:bg-(--vs-surface)! vptw:px-2 vptw:py-1 vptw:text-[11px] vptw:font-medium vptw:text-(--vs-ink-2)! vptw:cursor-pointer" @click="showMe(issue)">{{ t('Show me') }}</button>
                <button
                  v-if="issue.fix"
                  type="button"
                  class="vptw:rounded-md! vptw:border-0! vptw:px-2 vptw:py-1 vptw:text-[11px] vptw:font-semibold vptw:text-white! vptw:cursor-pointer"
                  :class="issue.severity === 'error' ? 'vptw:bg-red-600!' : 'vptw:bg-amber-600!'"
                  @click="fix([issue])"
                >{{ fixLabel(issue) }}</button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
